import type { CalculatorDefinition } from './types';
import { calculateBreakEvenDecision } from './breakEvenDecision';
import { calculateMarginDecision } from './marginDecision';
import { calculateRoasDecision } from './roasDecision';

function numberValue(value: unknown): number {
  return Number(value);
}

function optionalNumberValue(value: unknown): number {
  return value === undefined || value === '' ? 0 : Number(value);
}

function roundWon(value: number): number {
  return Math.round(value);
}

export const marginCalculator: CalculatorDefinition = {
  id: 'margin',
  slug: 'margin',
  title: '마진율 계산기',
  description: '판매가와 원가에 수수료·배송비·포장비·광고비 등 주문당 비용을 더해 실제 수익성을 계산하고 가격 변화를 비교합니다.',
  category: 'pricing',
  aliases: ['마진', '마진율', '이익률', '원가율', '수수료 마진', '배송비 마진', '광고비 마진'],
  inputs: [
    { id: 'salesPrice', label: '판매가', type: 'number', unit: '원', min: 1, step: 100, section: '기본 금액' },
    { id: 'cost', label: '원가', type: 'number', unit: '원', min: 0, step: 100, section: '기본 금액' },
    { id: 'platformFeeRate', label: '플랫폼 수수료율', type: 'percentage', unit: '%', min: 0, max: 100, step: 0.1, required: false, section: '추가 비용 (선택)' },
    { id: 'paymentFeeRate', label: '결제 수수료율', type: 'percentage', unit: '%', min: 0, max: 100, step: 0.1, required: false, section: '추가 비용 (선택)' },
    { id: 'shippingCost', label: '배송비', type: 'number', unit: '원', min: 0, step: 100, required: false, section: '추가 비용 (선택)' },
    { id: 'packagingCost', label: '포장비', type: 'number', unit: '원', min: 0, step: 100, required: false, section: '추가 비용 (선택)' },
    { id: 'adCost', label: '주문당 광고비', type: 'number', unit: '원', min: 0, step: 100, required: false, section: '추가 비용 (선택)' },
    { id: 'otherCost', label: '기타 주문당 비용', type: 'number', unit: '원', min: 0, step: 100, required: false, section: '추가 비용 (선택)' },
  ],
  outputs: [
    { id: 'profit', label: '주문당 예상 이익', unit: '원', format: 'currency', primary: true },
    { id: 'marginRate', label: '입력비용 반영 마진율', unit: '%', format: 'percentage' },
    { id: 'totalCost', label: '총 비용', unit: '원', format: 'currency' },
    { id: 'feeAmount', label: '수수료 금액', unit: '원', format: 'currency' },
    { id: 'costRate', label: '총 비용률', unit: '%', format: 'percentage' },
  ],
  calculate: (values) => {
    const result = calculateMarginDecision({
      salesPrice: numberValue(values.salesPrice),
      cost: numberValue(values.cost),
      platformFeeRate: optionalNumberValue(values.platformFeeRate),
      paymentFeeRate: optionalNumberValue(values.paymentFeeRate),
      shippingCost: optionalNumberValue(values.shippingCost),
      packagingCost: optionalNumberValue(values.packagingCost),
      adCost: optionalNumberValue(values.adCost),
      otherCost: optionalNumberValue(values.otherCost),
    });

    return {
      profit: result.profit,
      marginRate: result.marginRate,
      totalCost: result.totalCost,
      feeAmount: result.feeAmount,
      costRate: result.costRate,
    };
  },
  seo: {
    title: '마진율 계산기 | 수수료·배송비·광고비 포함 마진 계산',
    description: '판매가와 원가에 플랫폼·결제 수수료, 배송비, 포장비, 주문당 광고비를 반영해 예상 이익과 마진율을 계산하고 가격 변화 시나리오를 비교합니다.',
    canonicalPath: '/calculators/margin',
  },
  related: ['target-margin-price', 'discount-margin', 'break-even-roas', 'break-even', 'roas'],
};

export const targetMarginPriceCalculator: CalculatorDefinition = {
  id: 'target-margin-price',
  slug: 'target-margin-price',
  title: '목표 마진 판매가 계산기',
  description: '원가와 목표 마진율을 기준으로 필요한 판매가와 예상 이익을 계산합니다.',
  category: 'pricing',
  aliases: ['목표 마진', '판매가 계산', '적정 판매가', '마진 판매가'],
  inputs: [
    { id: 'cost', label: '원가', type: 'number', unit: '원', min: 0, step: 100 },
    { id: 'targetMarginRate', label: '목표 마진율', type: 'percentage', unit: '%', min: 0, max: 99.9, step: 0.1 },
  ],
  outputs: [
    { id: 'salesPrice', label: '필요 판매가', unit: '원', format: 'currency', primary: true },
    { id: 'profit', label: '예상 이익', unit: '원', format: 'currency' },
    { id: 'marginRate', label: '적용 마진율', unit: '%', format: 'percentage' },
  ],
  calculate: (values) => {
    const cost = numberValue(values.cost);
    const targetMarginRate = numberValue(values.targetMarginRate);
    const salesPrice = cost / (1 - targetMarginRate / 100);
    return {
      salesPrice: roundWon(salesPrice),
      profit: roundWon(salesPrice - cost),
      marginRate: targetMarginRate,
    };
  },
  seo: {
    title: '목표 마진 판매가 계산기 | 원하는 마진율로 판매가 계산',
    description: '원가와 목표 마진율을 입력하면 필요한 판매가와 예상 이익을 계산합니다.',
    canonicalPath: '/calculators/target-margin-price',
  },
  related: ['margin', 'vat', 'break-even'],
};

export const vatCalculator: CalculatorDefinition = {
  id: 'vat',
  slug: 'vat',
  title: '부가세 계산기',
  description: '공급가액에 부가세를 더하거나 부가세 포함 금액에서 공급가액을 역산합니다.',
  category: 'tax',
  aliases: ['부가세', 'VAT', '공급가액', '부가세 포함'],
  inputs: [
    {
      id: 'mode',
      label: '계산 방식',
      type: 'select',
      options: [
        { label: '공급가액 → 부가세 포함금액', value: 'add' },
        { label: '부가세 포함금액 → 공급가액', value: 'split' },
      ],
    },
    { id: 'amount', label: '금액', type: 'number', unit: '원', min: 0, step: 100 },
  ],
  outputs: [
    { id: 'supplyAmount', label: '공급가액', unit: '원', format: 'currency', primary: true },
    { id: 'vatAmount', label: '부가세', unit: '원', format: 'currency' },
    { id: 'totalAmount', label: '합계금액', unit: '원', format: 'currency' },
  ],
  calculate: (values) => {
    const amount = numberValue(values.amount);
    const mode = String(values.mode);
    if (mode === 'split') {
      const supplyAmount = amount / 1.1;
      return {
        supplyAmount: roundWon(supplyAmount),
        vatAmount: roundWon(amount - supplyAmount),
        totalAmount: roundWon(amount),
      };
    }
    const vatAmount = amount * 0.1;
    return {
      supplyAmount: roundWon(amount),
      vatAmount: roundWon(vatAmount),
      totalAmount: roundWon(amount + vatAmount),
    };
  },
  seo: {
    title: '부가세 계산기 | 공급가액·VAT·합계금액 계산',
    description: '공급가액 기준 부가세 계산과 부가세 포함 금액의 공급가액 역산을 지원합니다.',
    canonicalPath: '/calculators/vat',
  },
  related: ['margin', 'target-margin-price'],
};

export const breakEvenCalculator: CalculatorDefinition = {
  id: 'break-even',
  slug: 'break-even',
  title: '손익분기점 계산기',
  description: '고정비·판매단가·단위당 변동비로 손익분기점을 계산하고 목표이익과 현재 판매량, 비용 변화 시나리오까지 비교합니다.',
  category: 'business',
  aliases: ['손익분기점', 'BEP', '고정비', '공헌이익', '목표이익', '필요 판매량', '손익 시뮬레이션'],
  inputs: [
    { id: 'fixedCost', label: '고정비', type: 'number', unit: '원', min: 0, step: 1000, section: '기본 손익 구조' },
    { id: 'unitPrice', label: '판매단가', type: 'number', unit: '원', min: 1, step: 100, section: '기본 손익 구조' },
    { id: 'unitVariableCost', label: '단위당 변동비', type: 'number', unit: '원', min: 0, step: 100, section: '기본 손익 구조' },
    { id: 'targetProfit', label: '목표 이익', type: 'number', unit: '원', min: 0, step: 10000, required: false, section: '의사결정 입력 (선택)' },
    { id: 'currentUnits', label: '현재 판매량', type: 'number', unit: '개', min: 0, step: 1, required: false, section: '의사결정 입력 (선택)' },
  ],
  outputs: [
    { id: 'breakEvenUnits', label: '손익분기 판매량', unit: '개', format: 'integer', primary: true },
    { id: 'breakEvenRevenue', label: '손익분기 매출', unit: '원', format: 'currency' },
    { id: 'unitContribution', label: '단위당 공헌이익', unit: '원', format: 'currency' },
    { id: 'contributionMarginRate', label: '공헌이익률', unit: '%', format: 'percentage' },
  ],
  calculate: (values) => {
    const result = calculateBreakEvenDecision({
      fixedCost: numberValue(values.fixedCost),
      unitPrice: numberValue(values.unitPrice),
      unitVariableCost: numberValue(values.unitVariableCost),
      targetProfit: optionalNumberValue(values.targetProfit),
      currentUnits: values.currentUnits === undefined ? undefined : numberValue(values.currentUnits),
    });

    return {
      breakEvenUnits: result.breakEvenUnits,
      breakEvenRevenue: result.breakEvenRevenue === null ? null : roundWon(result.breakEvenRevenue),
      unitContribution: roundWon(result.unitContribution),
      contributionMarginRate: result.contributionMarginRate,
    };
  },
  seo: {
    title: '손익분기점 계산기 | BEP·목표이익 필요 판매량 계산',
    description: '고정비, 판매단가, 단위당 변동비로 손익분기 판매량과 매출을 계산하고 목표이익 달성 판매량, 현재 손익, 가격·비용 변화 시나리오를 비교합니다.',
    canonicalPath: '/calculators/break-even',
  },
  related: ['margin', 'target-margin-price', 'roas'],
};

export const roasCalculator: CalculatorDefinition = {
  id: 'roas',
  slug: 'roas',
  title: 'ROAS 계산기',
  description: '광고비와 광고 매출로 ROAS를 계산하고 마진 기준 손익분기점, 목표 ROAS, 광고비·매출 변화 시나리오까지 비교합니다.',
  category: 'advertising',
  aliases: ['ROAS', '광고수익률', '광고 효율', '광고비', '목표 ROAS', '손익분기 ROAS', '광고 손익'],
  inputs: [
    { id: 'adSpend', label: '광고비', type: 'number', unit: '원', min: 1, step: 1000, section: '광고 성과' },
    { id: 'revenue', label: '광고 매출', type: 'number', unit: '원', min: 0, step: 1000, section: '광고 성과' },
    { id: 'marginRate', label: '광고비 제외 마진율', type: 'percentage', unit: '%', min: 0.1, max: 100, step: 0.1, required: false, section: '의사결정 입력 (선택)' },
    { id: 'targetRoas', label: '목표 ROAS', type: 'percentage', unit: '%', min: 0.1, step: 10, required: false, section: '의사결정 입력 (선택)' },
  ],
  outputs: [
    { id: 'roas', label: 'ROAS', unit: '%', format: 'percentage', primary: true },
    { id: 'revenuePerWon', label: '광고비 1원당 매출', unit: '배', format: 'decimal' },
    { id: 'adCostRate', label: '매출 대비 광고비', unit: '%', format: 'percentage' },
  ],
  calculate: (values) => {
    const result = calculateRoasDecision({
      adSpend: numberValue(values.adSpend),
      revenue: numberValue(values.revenue),
      marginRate: values.marginRate === undefined ? undefined : numberValue(values.marginRate),
      targetRoas: values.targetRoas === undefined ? undefined : numberValue(values.targetRoas),
    });

    return {
      roas: result.roas,
      revenuePerWon: result.revenuePerWon,
      adCostRate: result.adCostRate,
    };
  },
  seo: {
    title: 'ROAS 계산기 | 손익분기·목표 ROAS 광고 효율 분석',
    description: '광고비와 광고 매출로 ROAS를 계산하고 광고비 제외 마진율 기준 손익분기 ROAS, 목표 ROAS 필요매출과 하방 시나리오를 비교합니다.',
    canonicalPath: '/calculators/roas',
  },
  related: ['break-even-roas', 'margin', 'break-even'],
};

export const phase2CalculatorDefinitions: readonly CalculatorDefinition[] = [
  marginCalculator,
  targetMarginPriceCalculator,
  vatCalculator,
  breakEvenCalculator,
  roasCalculator,
];
